import React, { useState } from 'react';
import { documentService } from '../../services/api';
import { Upload, File } from 'lucide-react';

const DocumentUpload = ({
  caseId,  // Now required - documents must be tied to a case
  projectId = null,
  folderId = null,
  onUploadSuccess = null,
  className = "",
  multiple = false,
  documentType = null
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);

  const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    setUploadQueue(fileArray.map(file => ({ file, status: 'pending', error: null })));
    setUploading(true);

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];

      // Client-side file size validation
      if (file.size > MAX_FILE_SIZE) {
        setUploadQueue(prev => prev.map((item, index) =>
          index === i ? { ...item, status: 'error', error: `File too large (${formatFileSize(file.size)}). Maximum size is 5GB.` } : item
        ));
        continue;
      }

      try {
        // Update status to uploading
        setUploadQueue(prev => prev.map((item, index) =>
          index === i ? { ...item, status: 'uploading' } : item
        ));

        const formData = new FormData();
        formData.append('file', file);
        formData.append('case_id', caseId);  // Always required now

        if (projectId) formData.append('project_id', projectId);
        if (folderId) formData.append('folder_id', folderId);
        if (documentType) formData.append('document_type', documentType);
        
        const response = await documentService.uploadDocument(formData);
        
        // Update status to success
        setUploadQueue(prev => prev.map((item, index) => 
          index === i ? { ...item, status: 'success', result: response.data } : item
        ));

        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        }
        
      } catch (error) {
        let errorMessage = 'Upload failed';
        if (error.response?.status === 413) {
          errorMessage = 'File too large. Maximum size is 5GB.';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (!error.response) {
          errorMessage = 'Network error — check your connection and try again.';
        }
        // Update status to error
        setUploadQueue(prev => prev.map((item, index) =>
          index === i ? { ...item, status: 'error', error: errorMessage } : item
        ));
      }
    }

    setUploading(false);
    
    // Clear queue after 3 seconds
    setTimeout(() => {
      setUploadQueue([]);
    }, 3000);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Don't render if no case ID provided
  if (!caseId) {
    return (
      <div className={`${className} text-center py-4`}>
        <p className="text-gray-500 text-sm">Document upload requires a case to be selected.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${dragActive 
            ? 'border-blue-400 bg-gray-50' 
            : uploading 
              ? 'border-gray-300 bg-gray-50' 
              : 'border-gray-300 hover:border-gray-400'
          }
          ${uploading ? 'cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && document.getElementById('file-input')?.click()}
      >
        <input
          id="file-input"
          type="file"
          multiple={multiple}
          onChange={handleInputChange}
          disabled={uploading}
          className="hidden"
        />

        <Upload className={`mx-auto h-12 w-12 ${uploading ? 'text-gray-400' : 'text-gray-500'}`} />

        <div className="mt-4">
          <p className={`text-sm ${uploading ? 'text-gray-500' : 'text-gray-700'}`}>
            {uploading ? 'Uploading...' : 'Drop files here or click to browse'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            All file types accepted (Max 5GB). AI processing available for PDF, Word, and Text files.
          </p>
        </div>
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className="mt-4 space-y-2">
          {uploadQueue.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <div className="flex items-center space-x-2">
                <File className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-700 truncate">
                  {item.file.name}
                </span>
                <span className="text-xs text-gray-500">
                  ({formatFileSize(item.file.size)})
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                {item.status === 'pending' && (
                  <span className="text-xs text-gray-500">Pending</span>
                )}
                {item.status === 'uploading' && (
                  <div className="flex items-center space-x-1">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-500"></div>
                    <span className="text-xs text-yellow-600">Uploading</span>
                  </div>
                )}
                {item.status === 'success' && (
                  <span className="text-xs text-green-600">✓ Success</span>
                )}
                {item.status === 'error' && (
                  <span className="text-xs text-red-600" title={item.error}>
                    ✗ Failed
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
